// "Login as merchant" — shared by the merchants list and a merchant's own page.
//
// This replaced revealing merchant passwords: passwords are argon2 hashes and cannot be read back,
// and the legacy plaintext file that the old Reveal screen read is a fixed snapshot that goes stale
// the moment anyone changes a password. Support opens the merchant's own portal instead.
import { api } from '@/api/client';

/**
 * Build the merchant-portal bootstrap URL for an impersonation token.
 * Prefers the partner.* / merchant.* host when the admin portal is on a sibling subdomain
 * (app.shiproute.demo → merchant.shiproute.demo), otherwise stays on the current origin.
 */
export function buildMerchantImpersonateUrl(payload: {
  token: string; id: number; email: string; companyName: string;
}): string {
  const params = new URLSearchParams({
    app: 'merchant',
    token: payload.token,
    id: String(payload.id),
    email: payload.email,
    companyName: payload.companyName,
  });
  const host = window.location.hostname;
  let origin = window.location.origin;
  if (!host.startsWith('partner.') && !host.startsWith('merchant.')) {
    // Skip the rewrite when there is no second-level parent (localhost, *.up.railway.app).
    const parts = host.split('.');
    if (parts.length >= 3 && !host.endsWith('.up.railway.app')) {
      origin = `${window.location.protocol}//partner.${parts.slice(1).join('.')}${window.location.port ? ':' + window.location.port : ''}`;
    }
  }
  return `${origin}/impersonate?${params.toString()}`;
}

/**
 * Exchange the admin's own password for a 6-hour merchant token and open the merchant portal in a
 * new tab. Throws with the server's message so the caller can toast it.
 */
export async function loginAsMerchant(
  merchant: { id: number; email: string; companyName: string },
  adminPassword: string,
): Promise<void> {
  const r = await api.post(`/admin/merchants/${merchant.id}/impersonate`, { adminPassword });
  const token: string = r.data?.accessToken;
  if (!token) throw new Error('No token returned');
  window.open(buildMerchantImpersonateUrl({
    token, id: merchant.id, email: merchant.email, companyName: merchant.companyName,
  }), '_blank');
}
